import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useEffect, PropsWithChildren, useState } from 'react'

import { tasAPI } from 'api'
import { loginRequest } from 'auth/config'

const apiInstances = [tasAPI]

interface ApiProviderProps extends PropsWithChildren {
  getToken?: () => Promise<string> // ← from root via customProps
}

export const ApiProvider = ({ children, getToken }: ApiProviderProps) => {
  const [isReady, setIsReady] = useState(false)
  const { instance, accounts } = useMsal()

  useEffect(() => {
    if (accounts.length === 0) return

    const account = accounts[0]

    // Shared token getter — acquireTokenSilent handles refresh automatically
    // replaces both osApi.getAccessToken() and useAZToken() entirely
    const getMsalToken = async (): Promise<string> => {
      if (getToken) {
        const token = await getToken()
        return token
      }
      try {
        const response = await instance.acquireTokenSilent({ ...loginRequest, account })
        return response.accessToken
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          window.location.href = `${process.env.REACT_APP_ROOT_URL}/login`
        }
        return ''
      }
    }

    const interceptors = apiInstances.map(apiInstance => {
      return apiInstance.client.interceptors.request.use(
        async config => {
          const token = await getMsalToken()

          if (token) {
            config!.headers!.Authorization = `Bearer ${token}`
            config.headers['Client'] = 'playground-dev-fe'
            config!.headers!['X-Tenant-Id'] = process.env.REACT_APP_X_TENANT_ID ?? ''
          }

          return config
        },
        error => Promise.reject(error),
      )
    })

    setIsReady(true)

    return () => {
      interceptors.forEach((interceptor, index) => {
        apiInstances[index].client.interceptors.request.eject(interceptor)
      })
    }
  }, [instance, accounts, getToken])

  if (!isReady) {
    return null
  }

  return <>{isReady && children}</>
}
